import { alpha, Box, Typography, useTheme } from "@mui/material";
import React, { useMemo, useState } from "react";
import { TreeItem, TreeItemProps } from "@mui/x-tree-view/TreeItem";
import { useTreeItem } from "@mui/x-tree-view/useTreeItem";
import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel.ts";
import { PartConfiguratorFormApi } from "src/api/generated/erp/configurator/api/partConfiguratorFormApi.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import { AavoTreeView } from "src/components/common/tree/AavoTreeView.tsx";
import { flatMapTreeItems } from "src/components/common/tree/AavoTreeView.utils.ts";
import {
	getAddableTreeItemSubConfiguratorComponents,
	TransformedSubConfiguratorOrListComponent,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/configuratorTreeItemQueries.ts";
import {
	assignCompatibleSubConfiguratorComponents,
	findFirstCompatibleSubConfiguratorComponent,
	getNonOverlappingSelectedSubConfigurations,
	SubConfigurationImportAssignment,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/subConfigurationImportUtils.ts";
import i18n from "i18next";
import {
	fetchCustomerOfferLinesWithConfiguration,
	SubConfigurationImportSourceOfferLine,
} from "src/components/views/erp/configurator/configuratorForm/impl/customerOfferLineSubConfigurationImportUtils.ts";
import { CustomerOfferLineImportSourceLineList } from "src/components/views/erp/configurator/configuratorForm/impl/CustomerOfferLineImportSourceLineList.tsx";

export interface CustomerOfferLineSubConfigurationImportViewProps {
	customerOfferId: number;
	currentCustomerOfferLineId?: number;
	targetParent: ConfiguratorTreeItemModel;
	closeDialog: () => Promise<void>;
	onSubmit: (assignments: SubConfigurationImportAssignment[]) => Promise<void>;
}

export function CustomerOfferLineSubConfigurationImportView({
	customerOfferId,
	currentCustomerOfferLineId,
	targetParent,
	closeDialog,
	onSubmit,
}: CustomerOfferLineSubConfigurationImportViewProps) {
	const [selectedLine, setSelectedLine] = useState<SubConfigurationImportSourceOfferLine>();
	const [selectedSourceItems, setSelectedSourceItems] = useState<ConfiguratorTreeItemModel[]>([]);
	const targetComponents = useMemo(() => getAddableTreeItemSubConfiguratorComponents(targetParent), [targetParent]);
	const assignments = useMemo(
		() => assignCompatibleSubConfiguratorComponents(targetComponents, selectedSourceItems),
		[targetComponents, selectedSourceItems],
	);
	const allSelectedItemsCanBeImported = assignments.length === selectedSourceItems.length;

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<AsyncFetchRender
				fetch={() => fetchCustomerOfferLinesWithConfiguration(customerOfferId, currentCustomerOfferLineId)}
				content={(sourceLines) => (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "minmax(240px, 35%) minmax(0, 1fr)" },
							minHeight: 420,
							flex: 1,
						}}
					>
						<CustomerOfferLineImportSourceLineList
							lines={sourceLines}
							selectedLineId={selectedLine?.customerOrderOfferLineId}
							selectLine={(line) => {
								setSelectedLine(line);
								setSelectedSourceItems([]);
							}}
						/>

						<VerticalBox sx={{ minWidth: 0, overflow: "auto" }}>
							{selectedLine == null ?
								<Typography color="text.secondary" sx={{ paddingX: 2 }}>
									{i18n.t("select_offer_line_first")}
								</Typography>
							:	<AsyncFetchRender
									key={selectedLine.customerOrderOfferLineId}
									fetch={() => fetchSourceTree(selectedLine.customerOrderOfferLineId)}
									content={(sourceRoot) => (
										<SourceConfigurationTree
											rootItem={sourceRoot}
											targetComponents={targetComponents}
											selectedItems={selectedSourceItems}
											onSelectionChanged={setSelectedSourceItems}
										/>
									)}
								/>
							}
							{selectedSourceItems.length > 0 && !allSelectedItemsCanBeImported && (
								<Typography color="error" sx={{ padding: 2 }}>
									{i18n.t("not_all_selected_sub_configurations_can_be_imported")}
								</Typography>
							)}
						</VerticalBox>
					</Box>
				)}
			/>

			<AavoFormFooter>
				<AsyncButton label={i18n.t("cancel")} color="secondary" onClick={closeDialog} />
				<AsyncButton
					label={i18n.t("import")}
					disabled={selectedSourceItems.length === 0 || !allSelectedItemsCanBeImported}
					onClick={async () => {
						if (assignments.length === 0 || !allSelectedItemsCanBeImported) return;
						await onSubmit(assignments);
					}}
				/>
			</AavoFormFooter>
		</VerticalBox>
	);

	async function fetchSourceTree(customerOrderOfferLineId: number): Promise<ConfiguratorTreeItemModel> {
		const result = await PartConfiguratorFormApi.getConfiguratorTreeModel({
			rootConfiguratorType: {
				type: "customerOrderOfferLine",
				customerOrderOfferLineId: customerOrderOfferLineId,
				explicitPartConfigurationId: null,
				testContextEnabled: false,
			},
			source: "SAVED_CONFIGURATION",
		});
		return result.treeModel.root;
	}
}

interface SourceTreeItem {
	id: string;
	label: string;
	isCompatible: boolean;
	sourceItem: ConfiguratorTreeItemModel;
	children: SourceTreeItem[];
}

function SourceConfigurationTree({
	rootItem,
	targetComponents,
	selectedItems,
	onSelectionChanged,
}: {
	rootItem: ConfiguratorTreeItemModel;
	targetComponents: TransformedSubConfiguratorOrListComponent[];
	selectedItems: ConfiguratorTreeItemModel[];
	onSelectionChanged: (items: ConfiguratorTreeItemModel[]) => void;
}) {
	const treeItems = useMemo(() => [mapSourceTreeItem(rootItem, targetComponents)], [rootItem, targetComponents]);
	const [expandedItems, setExpandedItems] = useState(() => flatMapTreeItems(treeItems).map((item) => item.id));
	const itemsById = useMemo(
		() => new Map(flatMapTreeItems(treeItems).map((item) => [item.id, item.sourceItem])),
		[treeItems],
	);

	return (
		<AavoTreeView<SourceTreeItem, true>
			items={treeItems}
			multiSelect
			checkboxSelection
			expandedItems={expandedItems}
			onExpandedItemsChange={setExpandedItems}
			selectedItems={selectedItems.map((item) => item.key)}
			onSelectedItemsChange={(_, selectedIds) => {
				const selectedIdSet = new Set(selectedIds);
				const selectedSourceItems =
					flatMapTreeItems(treeItems)
						.filter((item) => selectedIdSet.has(item.id))
						.map((item) => itemsById.get(item.id)!);
				onSelectionChanged(
					getNonOverlappingSelectedSubConfigurations(rootItem, selectedSourceItems),
				);
			}}
			isItemSelectionDisabled={(item) => !item.isCompatible}
			slots={{ item: SourceTreeItemView }}
			hideActionBar
			containerSx={{ paddingX: 1, paddingTop: 1 }}
		/>
	);
}

function mapSourceTreeItem(
	item: ConfiguratorTreeItemModel,
	targetComponents: TransformedSubConfiguratorOrListComponent[],
): SourceTreeItem {
	return {
		id: item.key,
		label: item.label,
		isCompatible: findFirstCompatibleSubConfiguratorComponent(targetComponents, item) != null,
		sourceItem: item,
		children: item.children.map((child) => mapSourceTreeItem(child, targetComponents)),
	};
}

const SourceTreeItemView = React.memo(function SourceTreeItemView(props: TreeItemProps) {
	const theme = useTheme();
	const { publicAPI } = useTreeItem(props);
	const item = publicAPI.getItem(props.itemId) as SourceTreeItem;

	return (
		<TreeItem
			{...props}
			sx={
				item.isCompatible ?
					{
						"& > .MuiTreeItem-content": {
							backgroundColor: alpha(theme.palette.success.main, 0.1),
							"&:hover": {
								backgroundColor: alpha(theme.palette.success.main, 0.16),
							},
							"&.Mui-selected, &.Mui-selected:hover": {
								backgroundColor: alpha(theme.palette.success.main, 0.24),
							},
						},
					}
				:	undefined
			}
		/>
	);
});
